<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <v-container fluid>
    <v-container grid-list-xl>
      <v-layout row wrap align-center>
        <v-flex xs12 sm3>
          <label class="label text-left w-100">{{ $language('pages.planning.departmentLabel') }}</label>
          <v-overflow-btn
            :value="department"
            :items="getDepartments(true)"
            item-text="Number"
            :return-object="true"
            editable
            clearable
            hide-details
            :label="$isLoading('departments') ? 'Loading...' : 'Choose Department'"
            :loading="$isLoading('departments')"
            @input="setDepartmentDebounce"
            @click:clear="setDepartmentDebounce(getDefaultDepartment())"
          ></v-overflow-btn>
        </v-flex>

        <v-flex xs12 sm3>
          <label class="label text-left w-100">{{ $language('pages.planning.projectLabel') }}</label>
          <v-overflow-btn
            :value="project"
            :items="getProjects(true)"
            item-text="Name"
            :return-object="true"
            editable
            clearable
            hide-details
            :label="$isLoading('projects') ? 'Loading...' : 'Choose Project'"
            :loading="$isLoading('projects')"
            @input="setProjectDebounce"
            @click:clear="setProjectDebounce(getDefaultProject())"
          ></v-overflow-btn>
        </v-flex>

        <v-flex xs12 sm3>
          <label class="label text-left w-100">{{ $language('pages.planning.filterLabel') }}</label>
          <v-overflow-btn
            :value="filter"
            :items="getFilters()"
            item-text="text"
            :return-object="true"
            editable
            clearable
            hide-details
            :label="$isLoading('filters') ? 'Loading...' : 'Choose Filter'"
            :loading="$isLoading('filters')"
            @input="setFilterDebounce"
            @click:clear="setFilterDebounce(getDefaultFilter())"
          ></v-overflow-btn>
        </v-flex>

        <v-flex xs12 sm3>
          <label class="label text-left w-100">{{ $language('pages.planning.searchLabel') }}</label>
          <v-text-field
            class="search-text-field"
            @input="setSearchDebounce"
            append-icon="search"
            :label="$language('pages.planning.searchPlaceholder')"
            single-line
            clearable
            :height="49"
            hide-details
            @click:clear="setSearchDebounce('')"
            :loading="$isLoading('search')"
          ></v-text-field>
        </v-flex>
      </v-layout>
    </v-container>

    <v-progress-linear v-if="$isLoading('created')" :height="2" :indeterminate="true"></v-progress-linear>

    <v-data-table
      v-else
      :headers="headers"
      :items="items"
      class="elevation-1 text-left w-100"
      :pagination.sync="pagination"
      :no-results-text="$language('table.noResults')"
      :no-data-text="$language('table.noRecords')"
      :rows-per-page-text="$language('table.pagination.rowsPerPage')"
      :rows-per-page-items="rows"
      :custom-filter="searchFilter"
      :search="search"
      :loading="$isLoading('departments')
        || $isLoading('projects')
        || $isLoading('filters')
        || $isLoading('search')"
    >
      <template v-slot:headers="{ headers }">
        <tr>
          <th>
            {{ $language('pages.planning.summary') }}
          </th>
          <th></th>
          <th></th>
          <th v-for="(month, columnIndex) in months" :key="columnIndex">
            {{ month.format }}
          </th>
        </tr>

        <tr class="tr-border">
          <th class="font-weight-bold font-size-14px black--text">
            {{ $language('pages.planning.availableHours') }}
          </th>
          <th></th>
          <th></th>
          <th v-for="(month, columnIndex) in months" :key="columnIndex" class="font-weight-bold font-size-14px black--text">
            {{ $helpers.formatNumber(availableHours[month.format]) }}
          </th>
        </tr>

        <tr class="tr-border">
          <th class="font-weight-bold font-size-14px black--text">
            {{ $language('pages.planning.plannedHours') }}
          </th>
          <th></th>
          <th></th>
          <th v-for="(month, columnIndex) in months" :key="columnIndex" class="font-weight-bold font-size-14px black--text">
            {{ $helpers.formatNumber(plannedHours[month.format]) }}
          </th>
        </tr>

        <tr class="tr-border">
          <th class="font-weight-bold font-size-14px black--text">
            {{ $language('pages.planning.occupancyRate') }}
          </th>
          <th></th>
          <th></th>
          <th v-for="(month, columnIndex) in months" :key="columnIndex" class="font-weight-bold font-size-14px black--text pa-0">
            <div
              class="text-field"
              :class="{ 'red--text': occupancyRate[month.format + '-red'] }"
            >
              {{ occupancyRate[month.format] }}
            </div>
          </th>
        </tr>

        <tr class="tr-border">
          <th
            v-for="header in headers"
            :key="header.text"
            :class="[
              header.sortable !== false ? 'column sortable' : '',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : '',
            ]"
            @click="header.sortable !== false && changeSort(header.value)"
          >
            {{ header.text }}
            <v-icon v-show="header.sortable !== false" small>arrow_upward</v-icon>
          </th>
        </tr>
      </template>
      <template slot="progress">
        <div class="v-progress-linear ma-0">
          <div class="v-progress-linear__bar">
            <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
              <div class="v-progress-linear__bar__indeterminate long primary"></div>
              <div class="v-progress-linear__bar__indeterminate short primary"></div>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:items="{ item: row, index: rowIndex }">
        <tr>
          <td>
            {{ row.Owner }}
          </td>

          <td>
            {{ row.Department }}
          </td>

          <td class="pa-0">
            <div
              @mouseover="showHoverProjectPopup($event.target, getProjectBy('ID', row.ProjectID))"
              @mouseout="hideHoverProjectPopup()"
              class="text-field no-wrap cursor-pointer"
              :class="'project-category-' + row.CategoryID"
            >
              {{ row.Project }}
            </div>
          </td>

          <td v-for="(month, columnIndex) in months" :key="columnIndex" class="pa-0">
            <input
              class="input-field"
              :class="{ 'input--error': row[month.format] === '' }"
              :ref="rowIndex + '-' + columnIndex"
              @blur="update($event.target, row, month.format)"
              @keydown="focusTo($event, rowIndex, columnIndex)"
              @focus="$event.target.select()"
              @keypress="$helpers.isInteger"
              type="number"
              :value="row[month.format]"
              min="0"
              step="1"
              lang="sv-SE"
            />
            <div v-if="$isLoading(row.DepartmentID + '-' + row.ProjectID + '-' + month.format)" class="v-progress-linear ma-0">
              <div class="v-progress-linear__bar">
                <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
                  <div class="v-progress-linear__bar__indeterminate long primary"></div>
                  <div class="v-progress-linear__bar__indeterminate short primary"></div>
                </div>
              </div>
            </div>
            <div v-else style="height: 1px;"></div>
          </td>
        </tr>
      </template>
    </v-data-table>

    <v-menu
      v-if="hoverProject.target"
      :value="true"
      :close-on-content-click="false"
      offset-x
      :close-delay="0"
      :activator="hoverProject.target"
    >
      <v-card @mouseover="keepShowingHoverProjectPopup()" @mouseout="hideHoverProjectPopup()">
        <v-card-title class="py-2" primary-title>
          <div class="w-100">
            <h4 class="mb-0 py-2">{{ hoverProject.project.Name }}</h4>
            <div class="w-100">
              <v-divider></v-divider>
              <span class="d-block py-1">
                {{ $language('pages.planning.card.owner') }}:
                {{ getDepartmentBy('ID', hoverProject.project.DepartmentID).Number }}
              </span>
              <span class="d-block py-1">
                {{ $language('pages.planning.card.category') }}:
                {{ getCategoryBy('ID', hoverProject.project.ProjectCategoryID).Name }}
              </span>
              <span class="d-block py-1">
                {{ $language('pages.planning.card.status') }}:
                {{ getStatusBy('ID', hoverProject.project.ProjectStatusID).Name }}
              </span>
              <span class="d-block py-1">
                {{ $language('pages.planning.card.orderValue') }}:
                {{ $helpers.formatNumber(hoverProject.project.OrderValue) }}
              </span>
              <span class="d-block py-1">
                {{ $language('pages.planning.card.departments') }}:
                {{ hoverProject.project.DepartmentIDs
                .map(ID => getDepartmentBy('ID', ID).Number)
                .join(', ') }}
              </span>
            </div>
          </div>
        </v-card-title>
      </v-card>
    </v-menu>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import tableNavigation from '@/mixin/tableNavigation';
import loading from '@/mixin/loading';
import search from '@/mixin/search';
import departments from '@/mixin/departments';
import projects from '@/mixin/projects';
import categories from '@/mixin/categories';
import statuses from '@/mixin/statuses';
import filters from '@/mixin/filters';
import planning from './planning';
import employees from '../Employees/employees';
import hoverProject from './hoverProject';

export default {
  name: 'planning',
  mixins: [tableNavigation, loading, search, departments, projects, categories, statuses, filters, planning, employees, hoverProject],
  data() {
    const months = this.$getMonths();
    return {
      months,
      headers: [
        { text: this.$language('pages.planning.table.owner'), value: 'Owner' },
        { text: this.$language('pages.planning.table.department'), value: 'Department' },
        { text: this.$language('pages.planning.table.project'), value: 'Project' },
        ...months.map(({ format }) => ({ text: format, sortable: false, value: format })),
      ],
      pagination: { rowsPerPage: 25 },
      rows: [25, 50, 100],
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    items() {
      const filters = Object.entries({
        DepartmentID: this.getDepartment('ID'),
        ProjectID: this.getProject('ID'),
      }).filter(([, value]) => typeof value !== 'undefined' && value !== null);
      let { planning } = this;
      if (filters.length) {
        planning = planning.filter((project) => {
          return filters.every(([field, value]) => {
            return project[field] === value;
          });
        });
      }
      if (this.getFilter('value') === 'missing-planning') {
        planning = planning.filter(planning => Object.values(planning).includes(''));
      }
      return planning;
    },
    availableHours() {
      return this.employees.availableHours[this.getDepartment('ID') || 'all'];
    },
    plannedHours() {
      return this.months.reduce((acc, { format: month }) => {
        acc[month] = this.items.reduce((acc, { [month]: hours }) => acc + parseInt(hours || 0, 10), 0);
        return acc;
      }, {});
    },
    occupancyRate() {
      return this.months.reduce((acc, { format: month }) => {
        if (this.availableHours[month] === '0') {
          acc[month] = '0%';
          if (this.plannedHours[month] > 0) {
            acc[`${month}-red`] = true;
          }
        } else {
          const plannedHours = this.plannedHours[month];
          const plannedHoursBig = this.$Big(plannedHours.toString().replace(/ /g, '').replace(',', '.'), 2);
          const availableHours = this.availableHours[month];
          const availableHoursBig = this.$Big(availableHours.toString().replace(/ /g, '').replace(',', '.'), 2);
          const occupancyRateBig = plannedHoursBig.div(availableHoursBig).times(100);
          const occupancyRate = parseFloat(occupancyRateBig.toFixed(2).toString());
          const availableLessThenPlanned = availableHoursBig.lt(plannedHoursBig);
          const sign = occupancyRate !== 0 && availableLessThenPlanned ? '-' : '';
          if (availableLessThenPlanned || sign) {
            acc[`${month}-red`] = true;
          }
          acc[month] = `${sign}${occupancyRate.toString().replace('.', ',')}%`;
        }
        return acc;
      }, {});
    },
  },
  methods: {
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
    },
    searchFilter(items, search) {
      if (!search) {
        return items;
      }
      const searchToLowerCase = search.toLowerCase();
      return items.filter(({ Owner, Department, Project }) => {
        return (Owner && Owner.toString().toLowerCase().includes(searchToLowerCase))
          || (Department && Department.toString().toLowerCase().includes(searchToLowerCase))
          || (Project && Project.toString().toLowerCase().includes(searchToLowerCase));
      });
    },
    async update(input, planning, month) {
      const oldValue = planning[month];
      const newValue = input.value ? parseFloat(input.value) : '';
      // value hasn't changed
      if (newValue === oldValue) {
        return;
      }
      try {
        await this.updatePlanning(planning, month, oldValue, newValue);
      } catch (err) {
        console.log(err);
        this.$store.commit('snackbar/show', (err.response && err.response.data && err.response.data.message) || err.message);
      }
    },
  },
  async created() {
    document.title = this.$language('pages.planning.title');
    this.$store.commit('page/title', this.$language('pages.planning.menuTitle'));
    this.setAllDepartmentsItem(this.$language('pages.planning.allDepartmentsItem'), this.$language('pages.planning.allDepartmentsItem'));
    this.setAllProjectsItem(this.$language('pages.planning.allProjectsItem'));
    this.setNoFilterItem(this.$language('pages.planning.noFilterItem'));
    this.setMissingPlanningItem(this.$language('pages.planning.missingPlanningItem'));
    this.$startLoading('created');
    try {
      const fetchDepartments = this.fetchDepartments();
      await Promise.all([
        this.fetchPlanning(this.fetchProjects(), fetchDepartments),
        this.fetchEmployees(fetchDepartments),
        this.fetchCategories(),
        this.fetchStatuses(),
      ]);
    } catch (err) {
      console.log(err);
      this.$store.commit('snackbar/show', (err.response && err.response.data && err.response.data.message) || err.message);
      throw err;
    }
    this.$stopLoading('created');
  },
};
</script>

<style lang="scss" scoped>
  .project-category-2 {
    background-color: #fbffdb;
  }
  .project-category-1 {
    background-color: #e5ffe5;
  }
  .red--text {
    color: #FF5252;
  }
  .tr-border {
    height: 49px;
    border-top: 1px solid white;
    border-bottom: 1px solid rgba(0,0,0,0.12);
    th > * {
      margin-bottom: 1px !important;
    }
  }
</style>
